import { useState, useEffect, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import themeGet from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';

import { Switch, Text as RadixText, Tooltip, Flex } from '@radix-ui/themes';
import Text from '../../../shared/Text';
import { useBroadcastContext } from '../contexts/BroadcastContext.jsx';
import { SmsPreview } from '../shared/SmsPreview.jsx';
import { TopHeader } from '../shared/Styles';
import { MMS_CHAR_LIMIT } from '../utils/constants';
import { useFetchCampaigns } from '../../campaigns/hooks/useFetchCampaigns';
import TinyMceEditor from '../../../shared/TinyMceEditor/TinyMceEditor';
import { useSmsPreview } from '../hooks/useSmsPreview';
import { RecipientFieldStrip } from './RecipientFieldStrip.jsx';
import { RecipientFieldDialog } from './RecipientFieldDialog.jsx';
import { buildInsertFieldItems } from '../utils/insertFieldItems';
import { buildLinkToAirboItems } from '../utils/linkToAirboItems';
import {
  NONEDITABLE_TOKEN_REGEXP,
  hasPrimaryAirboLink,
  replaceAirboLinkTokens,
} from '../utils/airboLinkTokens';
import { substituteRecipientTokens } from '../utils/recipientPreview';
import {
  stripUrlProtocolForCounting,
  normalizeSmsText,
} from '../utils/textHelpers';

export const Sms = () => {
  const theme = useTheme();
  const { t } = useTranslation('broadcasts');
  const {
    values,
    setFieldValue,
    linkTrackingDisabled,
    recipientFields = [],
    recipientRows = [],
    previewPerson,
    userCount,
  } = useBroadcastContext();
  const { campaigns, isFetching } = useFetchCampaigns({ for_broadcast: true });
  const [editorText, setEditorText] = useState(values.smsBody || '');
  const lastSetBody = useRef(values.smsBody);
  const { messageContent, previewMessage, wasNormalized } = useSmsPreview(
    values.smsBody,
  );
  const [showNewFieldDialog, setShowNewFieldDialog] = useState(false);
  // The live TinyMCE instance, so a field created from the Insert Field menu
  // can have its token dropped at the cursor as soon as it is saved.
  const editorRef = useRef(null);
  const insertToken = token => {
    const editor = editorRef.current;
    if (!editor) return;
    // Runs after the dialog unmounts and releases focus; TinyMCE needs the
    // editor focused for insertContent to land at the cursor.
    setTimeout(() => {
      editor.focus();
      editor.insertContent(`&nbsp;${token}&nbsp;`);
    }, 0);
  };

  // TinyMCE's `setup` only runs once, so `fetch` closures registered inside
  // it would otherwise capture the initial (stale) recipientFields/openDialog.
  // Keep them in refs and read the refs from `fetch`, which TinyMCE calls
  // fresh every time the menu opens.
  const recipientFieldsRef = useRef(recipientFields);
  useEffect(() => {
    recipientFieldsRef.current = recipientFields;
  }, [recipientFields]);

  const allowRecipientFields = (values.audiences || []).length > 0;
  const allowRecipientFieldsRef = useRef(allowRecipientFields);
  useEffect(() => {
    allowRecipientFieldsRef.current = allowRecipientFields;
  }, [allowRecipientFields]);

  const openDialogRef = useRef(() => setShowNewFieldDialog(true));
  useEffect(() => {
    openDialogRef.current = () => setShowNewFieldDialog(true);
  }, []);

  // Same reason for the Link to Airbo menu: whether a primary link is already
  // written (in either body) and which campaign it points at decide what
  // each menu item does, and both change after `setup` has run.
  const hasPrimaryLink =
    hasPrimaryAirboLink(values.smsBody) ||
    hasPrimaryAirboLink(values.emailBody);
  const airboLinkStateRef = useRef({
    campaignUuid: values.campaignUuid,
    hasPrimaryLink,
  });
  useEffect(() => {
    airboLinkStateRef.current = {
      campaignUuid: values.campaignUuid,
      hasPrimaryLink,
    };
  }, [values.campaignUuid, hasPrimaryLink]);

  // Same "Preview as" person as Review, shared via BroadcastContext. When
  // that resolves to no real recipient (e.g. "Me" isn't one), fall back to
  // the first recipient row so the phone still shows a real value.
  const firstRow = recipientRows[0];
  const firstRowPerson = firstRow && {
    userUuid: firstRow.userUuid,
    name: firstRow.name,
    firstName: (firstRow.name || '').split(' ')[0],
    employeeId: firstRow.employeeId,
  };
  const phonePreviewPerson = previewPerson?.userUuid
    ? previewPerson
    : firstRowPerson || previewPerson;

  const handleSms = (value, editor) => {
    const text = editor.getContent({ format: 'text' }).replace(/\n/g, '');
    const expanded = normalizeSmsText(
      replaceAirboLinkTokens(text, 'https://app.airbo.com/digest/sample-00000'),
    );
    const textLength = stripUrlProtocolForCounting(expanded).length;

    const isDeleting = text.length <= (lastSetBody.current || '').length;

    if (textLength <= MMS_CHAR_LIMIT || isDeleting) {
      setEditorText(value);
      lastSetBody.current = text;
      setFieldValue('smsBody', text);
    }
  };

  useEffect(() => {
    if (!isFetching && values.smsBody !== lastSetBody.current) {
      setEditorText(values.smsBody || '');
      lastSetBody.current = values.smsBody;
    }
  }, [isFetching, values.smsBody]);

  return (
    <>
      <TopHeader>
        <Text variant="h5">{t('sms.header')}</Text>
      </TopHeader>
      <Container>
        <Form>
          {!isFetching && (
            <TinyMceEditor
              value={editorText}
              onChange={handleSms}
              config={{
                noneditable_regexp: NONEDITABLE_TOKEN_REGEXP,
                placeholder: t('sms.placeholder'),
                plugins: ['lists', 'paste'],
                paste_as_text: true,
                verify_html: false,
                convert_urls: false,
                relative_urls: false,
                remove_script_host: false,
                toolbar: 'linkToAirbo insertField',
                content_style: `body { color: ${theme.colors.black['100']}; font-family:Montserrat,Arial,sans-serif; font-size:14px; margin: 0; -webkit-font-smoothing: antialiased; }`,
                setup: editor => {
                  editorRef.current = editor;
                  if (campaigns.length > 0) {
                    editor.ui.registry.addMenuButton('linkToAirbo', {
                      text: t('linkToAirbo'),
                      tooltip: t('linkTooltip'),
                      fetch: callback => {
                        callback(
                          buildLinkToAirboItems({
                            t,
                            campaigns,
                            campaignUuid:
                              airboLinkStateRef.current.campaignUuid,
                            hasPrimaryLink:
                              airboLinkStateRef.current.hasPrimaryLink,
                            setCampaignUuid: uuid =>
                              setFieldValue('campaignUuid', uuid),
                            insert: token =>
                              editor.insertContent(
                                `&nbsp;<span style="color: ${theme.colors.primary[100]}">${token}</span>&nbsp;`,
                              ),
                          }),
                        );
                      },
                    });
                  }

                  editor.ui.registry.addMenuButton('insertField', {
                    text: t('insertField.button'),
                    tooltip: t('insertField.tooltip'),
                    fetch: callback => {
                      callback(
                        buildInsertFieldItems({
                          t,
                          recipientFields: recipientFieldsRef.current || [],
                          recipientCount: userCount,
                          insert: token =>
                            // Plain text on purpose: a name or code should render in the
                            // surrounding text color, unlike the Airbo link token.
                            editor.insertContent(`&nbsp;${token}&nbsp;`),
                          openDialog: () => openDialogRef.current(),
                          allowRecipientFields: allowRecipientFieldsRef.current,
                        }),
                      );
                    },
                  });
                },
              }}
            />
          )}
          <EditorFooter data-testid="sms-editor-footer">
            {wasNormalized && (
              <NormalizedNotice>
                <Text variant="small3" color="black.60">
                  {t('sms.normalizedNotice')}
                </Text>
                <Tooltip content={t('sms.normalizedNoticeTooltip')}>
                  <i className="fa-regular fa-circle-info" />
                </Tooltip>
              </NormalizedNotice>
            )}
            <CharCounter data-testid="sms-char-counter">
              <Text variant="small3">
                {t('sms.charCount', {
                  count: messageContent.length,
                  limit: MMS_CHAR_LIMIT,
                })}
              </Text>
            </CharCounter>
          </EditorFooter>
          <RecipientFieldStrip body={values.smsBody} />
          {showNewFieldDialog && (
            <RecipientFieldDialog
              isOpen={showNewFieldDialog}
              onClose={() => setShowNewFieldDialog(false)}
              onCreated={field => insertToken(field.token)}
            />
          )}
          {!linkTrackingDisabled && (
            <TrackLinksSection>
              <SwitchRow>
                <Flex align="center" gap="2">
                  <RadixText size="2" weight="medium">
                    {t('sms.trackLinks')}
                  </RadixText>
                  <Tooltip content={t('sms.trackLinksTooltip')}>
                    <i className="fa-regular fa-circle-info" />
                  </Tooltip>
                </Flex>
                <Switch
                  name="trackLinks"
                  checked={values.trackLinks}
                  onCheckedChange={checked =>
                    setFieldValue('trackLinks', checked)
                  }
                  size="2"
                />
              </SwitchRow>
              {values.trackLinks && (
                <Disclaimer>
                  <Text variant="small3" color="black.60">
                    {t('sms.trackLinksDisclaimer')}
                  </Text>
                </Disclaimer>
              )}
            </TrackLinksSection>
          )}
        </Form>
        <Wrapper>
          <SmsPreview
            message={substituteRecipientTokens(previewMessage, {
              person: phonePreviewPerson,
              recipientFields,
              t,
            })}
          />
          {phonePreviewPerson?.name && (
            <SampleNote>
              <Text variant="small3" color="black.60">
                {t('sms.previewNote', { name: phonePreviewPerson.name })}
              </Text>
            </SampleNote>
          )}
        </Wrapper>
      </Container>
    </>
  );
};

const Form = styled.div`
  width: 50%;
  border-right: 1px solid ${themeGet('colors.black.10')};
  .tox-tinymce {
    border: none;

    .tox-editor-header {
      box-shadow: none !important;
    }

    .tox-edit-area::before {
      border: none;
    }

    .tox-edit-area {
      margin-left: 20px;
      margin-right: 20px;
      margin-bottom: 20px;
    }

    .tox-toolbar__primary {
      background: none;
      border-bottom: solid 1px;
      border-color: ${themeGet('colors.black.10')};

      .tox-toolbar__group {
        border-color: ${themeGet('colors.black.10')} !important;
      }
    }

    .tox-statusbar {
      display: none;
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: space-between;
`;

const EditorFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 4px 20px 8px;
`;

const CharCounter = styled.div`
  background: ${themeGet('colors.black.10')};
  border-radius: 4px;
  padding: 8px;
  flex: 0 0 auto;
`;

const NormalizedNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-right: auto;
  min-width: 0;
  color: ${themeGet('colors.black.60')};

  i {
    font-size: 12px;
  }
`;

const Wrapper = styled.div`
  width: 50%;
  max-height: 400px;
  overflow: hidden;
`;

const SampleNote = styled.div`
  text-align: center;
  margin-top: -8px;
`;

const TrackLinksSection = styled.div`
  padding: 16px 20px;
  border-top: 1px solid ${themeGet('colors.black.10')};
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Disclaimer = styled.div`
  margin-top: 8px;
`;
